import { Settings } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  CONGRESS_CHAMBER_DEFS,
  CONGRESS_TRADE_CATEGORY_DEFS,
  type CongressChamber,
  type CongressTradeCategory,
} from '@/utils/congressTradingTypes';

export function CongressTradingLayerSettings({
  layerActive,
  enabledCategories,
  enabledChambers,
  categoryCounts,
  chamberCounts,
  onEnabledCategoriesChange,
  onEnabledChambersChange,
}: {
  layerActive: boolean;
  enabledCategories: ReadonlySet<CongressTradeCategory>;
  enabledChambers: ReadonlySet<CongressChamber>;
  categoryCounts: ReadonlyMap<CongressTradeCategory, number>;
  chamberCounts: ReadonlyMap<CongressChamber, number>;
  onEnabledCategoriesChange: (categories: Set<CongressTradeCategory>) => void;
  onEnabledChambersChange: (chambers: Set<CongressChamber>) => void;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: MouseEvent) => {
      if (rootRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const toggleCategory = (category: CongressTradeCategory) => {
    const next = new Set(enabledCategories);
    if (next.has(category)) next.delete(category);
    else next.add(category);
    onEnabledCategoriesChange(next);
  };

  const toggleChamber = (chamber: CongressChamber) => {
    const next = new Set(enabledChambers);
    if (next.has(chamber)) next.delete(chamber);
    else next.add(chamber);
    onEnabledChambersChange(next);
  };

  const filtersActive =
    enabledCategories.size < CONGRESS_TRADE_CATEGORY_DEFS.length ||
    enabledChambers.size < CONGRESS_CHAMBER_DEFS.length;

  if (!layerActive) return null;

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        aria-label="Congress trading filters"
        aria-expanded={open}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`flex h-6 w-6 items-center justify-center rounded-md transition-colors focus:outline-none focus-visible:ring-1 focus-visible:ring-sky-400 ${
          open || filtersActive ? 'bg-sky-50 text-sky-800' : 'text-sky-700 hover:bg-sky-50'
        }`}
      >
        <Settings className="h-3.5 w-3.5" />
      </button>

      {open ? (
        <div
          role="dialog"
          aria-label="Congress trading filters"
          className="absolute left-0 top-full z-50 mt-1.5 w-56 rounded-lg border border-slate-200 bg-white p-2 shadow-lg"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="px-2 pb-1.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Chambers
          </p>
          <div className="mb-2 flex flex-col gap-0.5">
            {CONGRESS_CHAMBER_DEFS.map((def) => {
              const checked = enabledChambers.has(def.chamber);
              const count = chamberCounts.get(def.chamber) ?? 0;
              return (
                <label
                  key={def.chamber}
                  className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors ${
                    checked ? 'bg-sky-50/80' : 'hover:bg-slate-50'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggleChamber(def.chamber)}
                    className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                  />
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center justify-between gap-2">
                      <span className="text-xs font-medium text-slate-800">{def.label}</span>
                      {count > 0 ? (
                        <span className="shrink-0 text-[10px] tabular-nums text-slate-400">
                          {count.toLocaleString()}
                        </span>
                      ) : null}
                    </span>
                    <span className="block text-[10px] text-slate-500">{def.hint}</span>
                  </span>
                </label>
              );
            })}
          </div>

          <p className="border-t border-slate-100 px-2 pb-1.5 pt-2 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            Transaction types
          </p>
          <div className="institutional-holders-ribbon-scroll max-h-48 overflow-y-auto">
            <div className="flex flex-col gap-0.5">
              {CONGRESS_TRADE_CATEGORY_DEFS.map((def) => {
                const checked = enabledCategories.has(def.category);
                const count = categoryCounts.get(def.category) ?? 0;
                return (
                  <label
                    key={def.category}
                    className={`flex cursor-pointer items-start gap-2 rounded-md px-2 py-1.5 transition-colors ${
                      checked ? 'bg-sky-50/80' : 'hover:bg-slate-50'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => toggleCategory(def.category)}
                      className="mt-0.5 h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-sky-600 focus:ring-sky-400"
                    />
                    <span
                      className="mt-1 h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: def.color }}
                      aria-hidden
                    />
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium text-slate-800">{def.label}</span>
                        {count > 0 ? (
                          <span className="shrink-0 text-[10px] tabular-nums text-slate-400">
                            {count.toLocaleString()}
                          </span>
                        ) : null}
                      </span>
                      <span className="block text-[10px] text-slate-500">{def.hint}</span>
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          <div className="mt-1.5 flex gap-1 border-t border-slate-100 pt-1.5">
            <button
              type="button"
              className="flex-1 rounded-md px-2 py-1 text-[10px] font-medium text-sky-800 hover:bg-sky-50"
              onClick={() => {
                onEnabledCategoriesChange(
                  new Set(CONGRESS_TRADE_CATEGORY_DEFS.map((d) => d.category))
                );
                onEnabledChambersChange(new Set(CONGRESS_CHAMBER_DEFS.map((d) => d.chamber)));
              }}
            >
              Select all
            </button>
            <button
              type="button"
              className="flex-1 rounded-md px-2 py-1 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
              onClick={() => {
                onEnabledCategoriesChange(new Set());
                onEnabledChambersChange(new Set());
              }}
            >
              Clear all
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
